import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useBusiness } from "../context/BusinessContext";
import { useAuth, can } from "../context/AuthContext";
import Logo from "./Logo";
import {
  LayoutDashboard, Map, Wheat, Fuel, Route, FileText, BarChart3, Tractor,
  LineChart, Menu, X, Warehouse, Beef, Scale, TrendingUp, Wrench, Users, Receipt,
  ClipboardList, Truck, Package, LogOut, ShieldCheck, Sparkles, CalendarCheck, Bell,
} from "lucide-react";

const FARM_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", cap: "view.dashboard" },
  { to: "/fields", label: "Fields", icon: Map, testid: "nav-fields", cap: "view.fields" },
  { to: "/yields", label: "Yields", icon: Wheat, testid: "nav-yields", cap: "view.yields" },
  { to: "/inventory", label: "Inventory", icon: Warehouse, testid: "nav-inventory", cap: "view.inventory" },
  { to: "/livestock", label: "Livestock", icon: Beef, testid: "nav-livestock", cap: "view.livestock" },
  { to: "/expenses", label: "Expenses", icon: Fuel, testid: "nav-expenses", cap: "view.expenses" },
  { to: "/equipment", label: "Equipment", icon: Tractor, testid: "nav-equipment", cap: "view.equipment" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance", cap: "view.maintenance" },
  { to: "/mileage", label: "Mileage", icon: Route, testid: "nav-mileage", cap: "view.mileage_reports" },
  { to: "/field-pnl", label: "Field P&L", icon: BarChart3, testid: "nav-field-pnl", cap: "view.field_pnl" },
  { to: "/partners", label: "Partners", icon: Users, testid: "nav-partners", cap: "view.partner_invoices" },
  { to: "/year-compare", label: "Year Compare", icon: LineChart, testid: "nav-year-compare", cap: "view.reports" },
  { to: "/balance-sheet", label: "Balance Sheet", icon: Scale, testid: "nav-balance-sheet", cap: "view.balance_sheet" },
  { to: "/projections", label: "Projections", icon: TrendingUp, testid: "nav-projections", cap: "view.projections" },
  { to: "/reports", label: "Tax Reports", icon: FileText, testid: "nav-reports", cap: "view.reports" },
  { to: "/year-summary", label: "Year Summary", icon: Sparkles, testid: "nav-year-summary", cap: "view.reports" },
  { to: "/fmv-wizard", label: "FMV Wizard", icon: CalendarCheck, testid: "nav-fmv-wizard", cap: "view.balance_sheet" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications", cap: "manage.notifications" },
  { to: "/team", label: "Team & Roles", icon: ShieldCheck, testid: "nav-team-settings", cap: "manage.team" },
];

const REPAIR_NAV = [
  { to: "/repair", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-repair-dashboard", cap: "view.dashboard" },
  { to: "/repair/customers", label: "Customers", icon: Users, testid: "nav-repair-customers", cap: "view.invoices" },
  { to: "/repair/work-orders", label: "Work Orders", icon: ClipboardList, testid: "nav-repair-work-orders", cap: "view.invoices" },
  { to: "/repair/parts", label: "Parts", icon: Package, testid: "nav-repair-parts", cap: "view.invoices" },
  { to: "/repair/invoices", label: "Invoices", icon: Receipt, testid: "nav-repair-invoices", cap: "view.invoices" },
  { to: "/repair/expenses", label: "Expenses", icon: Fuel, testid: "nav-repair-expenses", cap: "view.expenses" },
  { to: "/repair/customer-pnl", label: "Customer P&L", icon: BarChart3, testid: "nav-repair-customer-pnl", cap: "view.customer_pnl" },
];

const TRUCKING_NAV = [
  { to: "/trucking", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-trucking-dashboard", cap: "view.dashboard" },
  { to: "/trucking/customers", label: "Customers", icon: Users, testid: "nav-trucking-customers", cap: "view.invoices" },
  { to: "/trucking/fleet", label: "Fleet", icon: Truck, testid: "nav-trucking-fleet", cap: "view.fleet_pnl" },
  { to: "/trucking/loads", label: "Loads", icon: Package, testid: "nav-trucking-loads", cap: "view.invoices" },
  { to: "/trucking/invoices", label: "Invoices", icon: Receipt, testid: "nav-trucking-invoices", cap: "view.invoices" },
  { to: "/trucking/expenses", label: "Expenses", icon: Fuel, testid: "nav-trucking-expenses", cap: "view.expenses" },
  { to: "/trucking/customer-pnl", label: "Customer P&L", icon: BarChart3, testid: "nav-trucking-customer-pnl", cap: "view.customer_pnl" },
];

const BRAND_BY_BUSINESS = {
  farm: { tag: "ACRELATCH", sub: "Farm Ops & Tax Log", icon: null, accent: "#1F2A47", useLogo: true },
  jk_repair: { tag: "J&K REPAIR", sub: "Heavy Equipment", icon: Wrench, accent: "#BC4727" },
  jk_trucking: { tag: "J&K TRUCKING", sub: "On-time Freight", icon: Truck, accent: "#BC4727" },
};

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { active } = useBusiness();
  const { user, logout } = useAuth();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const rawNav = active === "jk_repair" ? REPAIR_NAV : active === "jk_trucking" ? TRUCKING_NAV : FARM_NAV;
  const nav = rawNav.filter((item) => !item.cap || can(user, item.cap));
  const brand = BRAND_BY_BUSINESS[active] || BRAND_BY_BUSINESS.farm;
  const BrandIcon = brand.icon;
  const BrandMark = ({ size = 36 }) => (
    brand.useLogo
      ? <Logo size={size} className="rounded-sm" />
      : (
        <div className="flex items-center justify-center" style={{ background: brand.accent, width: size, height: size }}>
          <BrandIcon className="text-white" style={{ width: size * 0.55, height: size * 0.55 }} strokeWidth={2.4} />
        </div>
      )
  );

  return (
    <>
      <div
        className="no-print md:hidden sticky top-0 z-40 bg-[#1F2A47] text-[#F5F4F0] flex items-center justify-between px-4 py-3 border-b border-white/5"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-2">
          <BrandMark size={32} />
          <div className="brand-tag text-base leading-none">{brand.tag}</div>
        </div>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          className="no-print md:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`no-print bg-[#1F2A47] text-[#F5F4F0] w-64 shrink-0 flex flex-col fixed md:sticky md:top-0 md:h-screen top-0 left-0 h-full z-50 transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-white/5 hidden md:block">
          <div className="flex items-center gap-3">
            <BrandMark size={44} />
            <div>
              <div className="brand-tag text-xl leading-none text-white">{brand.tag}</div>
              <div className="text-[10px] tracking-[0.14em] text-slate-400 mt-1 font-medium">
                {brand.sub}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto px-3">
          {nav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 text-[13px] font-semibold rounded-lg mb-0.5 transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-[#C8C5B8] hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4 opacity-80" strokeWidth={2.2} />
              {label}
            </NavLink>
          ))}
        </nav>

        {user ? (
          <div className="px-4 py-4 border-t border-white/5" data-testid="sidebar-user">
            <div className="flex items-center gap-2.5 mb-3 px-1">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/10" />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-[#BC4727] to-[#9D391E] rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate text-white">{user.name || user.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">{user.role}</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-[11px] uppercase tracking-wider font-semibold py-2 rounded-lg"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        ) : (
          <div className="px-4 py-4 border-t border-white/5" data-testid="sidebar-bootstrap">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 px-1">Unclaimed farm</div>
            <a
              href="/login"
              data-testid="sidebar-claim-btn"
              className="block text-center w-full bg-[#BC4727] hover:bg-[#9D391E] text-white text-[11px] uppercase tracking-wider font-semibold py-2 rounded-lg shadow-lg shadow-[#BC4727]/20"
            >
              Sign in to claim
            </a>
          </div>
        )}

        <div className="px-6 py-3 border-t border-white/5 text-[9px] tracking-wider text-slate-500 uppercase">
          Multi-Business · v3
        </div>
      </aside>
    </>
  );
}
